import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { usePermissions } from '@/hooks/usePermissions';

export function useBetaCheckin() {
  const { activeShop } = useAuth();
  const { isManager, isOwner } = usePermissions();
  const isManagerRole = isManager || isOwner;
  const shopId = activeShop?.id;

  const { data } = useQuery({
    queryKey: ['beta-checkin-status', shopId],
    queryFn: async () => {
      if (!shopId) return null;

      const { data, error } = await supabase
        .from('shops')
        .select('beta_signed_at, beta_expires_at, beta_next_checkin_at, beta_status, beta_checkin_count')
        .eq('id', shopId)
        .single();

      if (error || !data) return null;
      return data;
    },
    enabled: !!shopId && isManagerRole,
    staleTime: 60000,
  });

  if (!data || !isManagerRole || !shopId) {
    return { shouldShowCheckin: false, dayNumber: 0, daysRemaining: 0, betaStatus: null, shopId: null };
  }

  const { beta_signed_at, beta_expires_at, beta_next_checkin_at, beta_status } = data;

  if (!beta_signed_at || !beta_expires_at || !beta_next_checkin_at) {
    return { shouldShowCheckin: false, dayNumber: 0, daysRemaining: 0, betaStatus: beta_status, shopId };
  }

  const now = new Date();
  const signedAt = new Date(beta_signed_at);
  const expiresAt = new Date(beta_expires_at);
  const nextCheckin = new Date(beta_next_checkin_at);

  const dayNumber = Math.max(1, Math.ceil((now.getTime() - signedAt.getTime()) / (24 * 60 * 60 * 1000)));
  const daysRemaining = Math.max(0, Math.ceil((expiresAt.getTime() - now.getTime()) / (24 * 60 * 60 * 1000)));

  const activeStatuses = ['active', 'extended', 'completed_awaiting_decision'];
  const isActiveStatus = activeStatuses.includes(beta_status);
  const isDue = now >= nextCheckin;
  const dismissed = sessionStorage.getItem(`beta-checkin-dismissed-${shopId}`);

  return {
    shouldShowCheckin: isActiveStatus && isDue && !dismissed,
    dayNumber,
    daysRemaining,
    betaStatus: beta_status,
    shopId,
  };
}
